import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useTherapist } from "../../../context/useTherapist";

import { CenterSubscriptionAPI } from "../infrastructure/api/CenterSubscription.api";
import {
    CenterSubscriptionState,
    toCenterSubscriptionState,
} from "../domain/centerTypes";
import { BillingPeriod, CancellationReason, PlanTier, toCoupon } from "../domain/types";

export const CENTER_SUBSCRIPTION_KEY = "center-subscription";

export const useCenterSubscription = (centerId: string | undefined) =>
    useQuery<CenterSubscriptionState>({
        queryKey: [CENTER_SUBSCRIPTION_KEY, centerId],
        queryFn: async () => {
            const { data } = await CenterSubscriptionAPI.state(centerId as string);
            return toCenterSubscriptionState(data);
        },
        enabled: Boolean(centerId),
    });

/** Buy seats: the period, and how many the centre chose. */
export const useStartCenterCheckout = (centerId: string | undefined) =>
    useMutation({
        mutationFn: async ({
            period,
            seats,
            coupon,
            modules = [],
            tier = "plus",
        }: {
            period: BillingPeriod;
            seats: number;
            coupon?: string;
            /** Paid modules bought with the seats, each across every seat. */
            modules?: string[];
            /** Plus or Max, for every seat. */
            tier?: PlanTier;
        }) => {
            const { data } = await CenterSubscriptionAPI.checkout(
                centerId as string,
                period,
                seats,
                coupon,
                modules,
                tier
            );
            window.location.assign(data.url);
        },
    });

/** Check a discount code before the centre pays. Never cached: see the
 * therapist's. */
export const useCheckCenterCoupon = (centerId: string | undefined) =>
    useMutation({
        mutationFn: async (code: string) => {
            const { data } = await CenterSubscriptionAPI.coupon(
                centerId as string,
                code
            );
            return toCoupon(data);
        },
    });

/**
 * Change how much capacity the centre pays for.
 *
 * Refetches rather than trusting the response: this also changes how many
 * seats are free, and the server refuses a count below what is in use — a
 * screen that assumed success would show a number the centre does not have.
 */
export const useSetCenterSeats = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (seats: number) =>
            CenterSubscriptionAPI.setSeats(centerId as string, seats),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CENTER_SUBSCRIPTION_KEY, centerId],
            });
            // The team screen shows seats left, from a different endpoint.
            queryClient.invalidateQueries({ queryKey: ["center-seats"] });
        },
    });
};

export const useCancelCenterSubscription = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (why: { reason?: CancellationReason; comment?: string } = {}) =>
            CenterSubscriptionAPI.cancel(centerId as string, why.reason, why.comment),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CENTER_SUBSCRIPTION_KEY, centerId],
            }),
    });
};

/** Move every seat between Plus and Max: up now, down at the renewal. */
export const useChangeCenterTier = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    // The administrator sits in a seat too, so their own size moves with it.
    const { refreshTherapist } = useTherapist();
    return useMutation({
        mutationFn: (tier: PlanTier) =>
            CenterSubscriptionAPI.changeTier(centerId as string, tier),
        onSuccess: async () => {
            queryClient.invalidateQueries({
                queryKey: [CENTER_SUBSCRIPTION_KEY, centerId],
            });
            await refreshTherapist();
        },
    });
};

export const useResumeCenterSubscription = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => CenterSubscriptionAPI.resume(centerId as string),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CENTER_SUBSCRIPTION_KEY, centerId],
            }),
    });
};

export const useOpenCenterBillingPortal = (centerId: string | undefined) =>
    useMutation({
        mutationFn: async (updatePaymentMethod: boolean | void) => {
            const { data } = await CenterSubscriptionAPI.billingPortal(
                centerId as string,
                Boolean(updatePaymentMethod)
            );
            window.location.assign(data.url);
        },
    });
